/**
 * GroupTabBar — the tab strip for the full-page GROUP record (/families/:id).
 *
 * This is the SAME L1A `StageTabBar` mechanism the Contact record uses
 * (ContactTabBar): identical `.stagebar contact-tabs` classes, `role="tablist"`
 * + roving tabindex + ArrowLeft/Right/Home/End keyboard model, and the proven
 * mobile horizontal chip-rail (the `.stagebar` `@media max-width:1080px` block in
 * components.css). It is a THIN, group-specific bar rather than a direct reuse of
 * ContactTabBar only because the tab KEYS differ (a Family has 4 tabs, an
 * Organisation adds Billing → 5). Behaviour is copied verbatim so the two records
 * feel identical and the mobile swipe-rail carries over unchanged.
 */
import { useRef, type KeyboardEvent } from 'react';

export type GroupTabKey =
  | 'details'
  | 'members'
  | 'structure'
  | 'astro'
  | 'billing';

export interface GroupTabItem {
  key: GroupTabKey;
  label: string;
  /** Emoji icon rendered in the `.dot` slot (group tabs are icon tabs). */
  icon: string;
}

/** Family record tabs — no Billing. Default active tab is Astro-Vastu. */
export const FAMILY_TABS: GroupTabItem[] = [
  { key: 'details', label: 'Details', icon: '📄' },
  { key: 'members', label: 'Members', icon: '👥' },
  { key: 'structure', label: 'Structure chart', icon: '🌳' },
  { key: 'astro', label: 'Astro-Vastu', icon: '🔯' },
];

/** Organisation record tabs — Members reads "Key personnel"; adds Billing. */
export const ORG_TABS: GroupTabItem[] = [
  { key: 'details', label: 'Details', icon: '📄' },
  { key: 'members', label: 'Key personnel', icon: '👥' },
  { key: 'structure', label: 'Structure chart', icon: '🏛️' },
  { key: 'astro', label: 'Astro-Vastu', icon: '🔯' },
  { key: 'billing', label: 'Billing', icon: '🧾' },
];

export function GroupTabBar({
  items,
  activeKey,
  onSelect,
  panelId = 'group-panel',
}: {
  items: GroupTabItem[];
  activeKey: GroupTabKey;
  onSelect: (key: GroupTabKey) => void;
  /** id of the single tabpanel this strip controls (aria-controls). */
  panelId?: string;
}) {
  const keys = items.map((i) => i.key);
  // Refs to each tab button so keyboard nav can MOVE DOM focus to the newly
  // selected tab (mirrors ContactTabBar). Without this, focus strands on the old
  // tab which becomes tabindex=-1 (a11y gate-blocker).
  const btnRefs = useRef<Array<HTMLButtonElement | null>>([]);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const idx = keys.indexOf(activeKey);
    if (idx < 0) return;
    let next = -1;
    if (e.key === 'ArrowRight') next = (idx + 1) % keys.length;
    else if (e.key === 'ArrowLeft') next = (idx - 1 + keys.length) % keys.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = keys.length - 1;
    if (next >= 0) {
      e.preventDefault();
      onSelect(keys[next]);
      const btn = btnRefs.current[next];
      if (btn) {
        btn.focus();
        btn.scrollIntoView({ inline: 'center', block: 'nearest' });
      }
    }
  }

  return (
    <div
      className="stagebar contact-tabs"
      role="tablist"
      aria-orientation="horizontal"
      aria-label="Group record sections"
      onKeyDown={onKeyDown}
    >
      {items.map((it, i) => {
        const active = it.key === activeKey;
        return (
          <button
            key={it.key}
            ref={(el) => {
              btnRefs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={`gtab-${it.key}`}
            aria-selected={active}
            aria-controls={panelId}
            tabIndex={active ? 0 : -1}
            className={`nav${active ? ' on' : ''}`}
            title={it.label}
            onClick={(e) => {
              onSelect(it.key);
              (e.currentTarget as HTMLElement).scrollIntoView({
                inline: 'center',
                block: 'nearest',
              });
            }}
          >
            <span className="dot" aria-hidden>
              {it.icon}
            </span>
            <span className="lbl">{it.label}</span>
          </button>
        );
      })}
    </div>
  );
}
